import { useEffect } from 'react'
import { TimeStatus } from '@/models/generated/graphql'

const JOIN_WINDOW_MINUTES = 10
const REFRESH_BUFFER_SECONDS = 10

type UseAppointmentPageRefreshParams = {
  appointmentFrom: string // ISO8601 date
  currentTimeStatus: TimeStatus
}

/**
 * Custom hook to schedule automatic page refresh 10 minutes before appointment start.
 *
 * Reloads the page at (appointmentStartTime - 10 minutes + 10 seconds) to reinitialize:
 * - GraphQL queries
 * - Device permissions (microphone/camera)
 * - Chime session
 *
 * This timing corresponds to the join window, allowing users to access the meeting
 * 10 minutes before it officially starts.
 *
 * Only operates when currentTimeStatus is Future. Does nothing for Current/Past appointments.
 *
 * @param appointmentFrom - ISO8601 appointment start date
 * @param currentTimeStatus - TimeStatus from backend GraphQL query
 *
 * @example
 * useAppointmentPageRefresh({
 *   appointmentFrom: appointmentData.from,
 *   currentTimeStatus: appointmentData.timeStatus
 * })
 * // For an appointment at 14:00, page will refresh at 13:50:10
 */
export function useAppointmentPageRefresh({
  appointmentFrom,
  currentTimeStatus
}: UseAppointmentPageRefreshParams): void {
  useEffect(() => {
    // Only schedule refresh if appointment is in the future
    if (currentTimeStatus !== TimeStatus.Future) {
      return
    }

    const now = new Date()
    const meetingStart = new Date(appointmentFrom)

    // Handle invalid date
    if (isNaN(meetingStart.getTime())) {
      console.warn('[useAppointmentPageRefresh] Invalid appointment date:', appointmentFrom)
      return
    }

    // Calculate refresh time: 10 minutes before appointment start + buffer
    const joinWindowStart = new Date(meetingStart.getTime() - JOIN_WINDOW_MINUTES * 60 * 1000)
    const refreshTime = new Date(joinWindowStart.getTime() + REFRESH_BUFFER_SECONDS * 1000)
    const delay = refreshTime.getTime() - now.getTime()

    // Don't schedule if refresh time is in the past
    if (delay <= 0) {
      console.warn('[useAppointmentPageRefresh] Refresh time is in the past, not scheduling refresh')
      return
    }

    console.log(
      `[useAppointmentPageRefresh] Scheduling page refresh in ${Math.round(delay / 1000)}s at ${refreshTime.toLocaleTimeString()}`
    )

    // Schedule page refresh
    const timeoutId = setTimeout(() => {
      console.log('[useAppointmentPageRefresh] Refreshing page now...')
      window.location.reload()
    }, delay)

    // Cleanup timeout on unmount or dependency change
    return () => {
      console.log('[useAppointmentPageRefresh] Clearing scheduled refresh')
      clearTimeout(timeoutId)
    }
  }, [appointmentFrom, currentTimeStatus])
}
